import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, createRoute, redirect, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PREFIXO_API, ROTAS } from '@remoto/shared';
import { rotaRaiz } from './raiz';
import { chamar, garantirSessao } from '../api';
import { usarSessao } from '../estado/sessao';
import { aoReconectar, fecharSocketDoPainel, ligarDespertar, socketDoPainel } from '../socket/painel';
import { sincronizarAvisos } from '../notificacoes/push';
import { Shell, usarTelaLarga } from '../workspace/Shell';
import { Explorador } from '../workspace/Explorador';
import { ListaDeSessoes, type SessaoNaLista } from '../workspace/ListaDeSessoes';
import { PainelDaSessao, type Sessao } from '../componentes/chat/PainelDaSessao';
import { ProvedorDoWorkspace } from '../workspace/contexto';
import { abrirAba, escreverWorkspace, type EstadoDoWorkspace } from '../workspace/estado';
import {
  estadoInicial,
  fatiaDaSessao,
  lembrarFatiaDaSessao,
  lembrarWorkspace,
} from '../workspace/memoria';
import { TelaDaProsa } from '../prosa/TelaDaProsa';
import { usarSessaoMestre } from '../sessoes/mestre';
import { lerMotorDaProsa, mestreDaProsa } from '../prosa/escolha';

/**
 * A moldura do painel, como rota de layout.
 *
 * Antes existiam duas molduras — uma na tela de trabalho, outra em volta das
 * telas gerais — e trocar de página desmontava a moldura inteira para montar a
 * outra: o socket reassinava, o histórico recomeçava e a rolagem voltava ao
 * topo. Aqui ela monta **uma vez**. Quem troca na navegação é só o `Outlet` do
 * centro; a lateral e a conversa continuam vivas, e nas telas gerais elas são
 * escondidas encolhendo o painel, nunca desmontando.
 */
function Painel(): React.JSX.Element {
  const busca = useSearch({ strict: false }) as Record<string, unknown>;
  const navegar = useNavigate();
  const telaLarga = usarTelaLarga();
  const rota = useRouterState({ select: (e) => e.location.pathname });
  const naTelaDeTrabalho = rota === '/';
  const { mestres } = usarSessaoMestre();
  // A Prosa abre com o mestre PREFERIDO (E26): sem isto, a escolha
  // guardada so valeria quando a sessao ja estivesse na URL.
  const mestre = mestreDaProsa(mestres, lerMotorDaProsa());

  // Fora da tela de trabalho a URL não carrega estes parâmetros; quem lembra a
  // pasta, as abas e a sessão é o navegador.
  const estado = estadoInicial(busca);

  // Arquivos que a sessão tocou nesta visita: ganham marca na árvore. Somem ao
  // recarregar de propósito — é um "olha o que acabou de mudar", não histórico.
  const [tocados, setTocados] = useState<Set<string>>(new Set());

  const sair = usarSessao((e) => e.sair);
  const acesso = usarSessao((e) => e.acesso);
  const motivoSaida = usarSessao((e) => e.motivoSaida);

  /**
   * Saída FORÇADA com o painel aberto (login único: a conta entrou em outro
   * aparelho). A guarda de rota só roda ao navegar — sem isto a tela ficava
   * de pé, muda, com o socket morto. Levar para /entrar é o que põe o aviso
   * do motivo na frente da pessoa.
   */
  useEffect(() => {
    if (!acesso && motivoSaida) void navegar({ to: '/entrar' });
  }, [acesso, motivoSaida, navegar]);

  // A mesma chave que o painel da conversa usa: o cache do React Query serve
  // os dois, e a segunda leitura não custa requisição nenhuma.
  const { data: sessaoAtual } = useQuery({
    queryKey: ['sessao', estado.sessao],
    queryFn: () => chamar<{ cwd: string }>(`${ROTAS.sessoes}/${estado.sessao}`),
    enabled: Boolean(estado.sessao),
  });

  const ir = useCallback(
    (proximo: EstadoDoWorkspace) => {
      lembrarWorkspace(proximo);
      // Explorador e abas ficam presos à sessão: voltar a uma conversa de
      // ontem tem de reencontrar a pasta e os arquivos daquele trabalho.
      lembrarFatiaDaSessao(proximo.sessao, proximo);
      void navegar({ to: '/', search: escreverWorkspace(proximo), replace: true });
    },
    [navegar],
  );

  /**
   * Trocar de sessão troca o explorador e as abas junto.
   *
   * Se a sessão nunca foi aberta aqui, ela começa na própria pasta de trabalho
   * — que é onde a pessoa ia procurar de qualquer jeito — em vez de na
   * primeira raiz permitida, que numa máquina de trabalho pode ser uma pasta
   * de clientes com mil itens.
   */
  function trocarDeSessao(nova: SessaoNaLista): void {
    const guardada = fatiaDaSessao(nova.id);
    ir({
      ...estado,
      sessao: nova.id,
      maquina: guardada?.maquina ?? nova.maquinaId,
      pasta: guardada?.pasta ?? nova.cwd,
      abas: guardada?.abas ?? [],
      aba: guardada?.aba ?? '',
    });
  }

  /**
   * Mantém a URL falando o mesmo que o estado — só na tela de trabalho.
   *
   * Nas telas gerais a URL é a da tela, e reescrevê-la aqui jogaria a pessoa de
   * volta para `/` a cada render.
   */
  useEffect(() => {
    lembrarWorkspace(estado);
    if (!naTelaDeTrabalho) return;
    const daUrl = escreverWorkspace(estado);
    const igual = Object.entries(daUrl).every(([chave, valor]) => busca[chave] === valor);
    if (!igual) void navegar({ to: '/', search: daUrl, replace: true });
    // A dependência é o conteúdo do estado, não o objeto: ele é derivado da URL
    // a cada render e nunca é o mesmo por identidade.
  }, [JSON.stringify(estado), naTelaDeTrabalho]);

  /**
   * Voltar do sono não é continuar de onde parou.
   *
   * Enquanto o aparelho dormia, o socket morreu e o que aconteceu na sessão não
   * chegou a ninguém. O socket.io reconecta e as assinaturas se refazem — mas o
   * intervalo em que estivemos fora só volta pedindo de novo. Sem isto, o tablet
   * acorda mostrando o mundo do momento em que apagou a tela.
   */
  const cliente = useQueryClient();
  useEffect(() => {
    socketDoPainel();
    const soltarDespertar = ligarDespertar();
    const soltarReconexao = aoReconectar(() => {
      // Renovar primeiro, pelo mesmo motivo da volta à tela: o socket que
      // caiu costuma ter caído junto com o access, e invalidar tudo antes
      // faria toda consulta sair com o token morto para descobrir isso.
      void garantirSessao().finally(() => void cliente.invalidateQueries());
    });
    /**
     * Todo aparelho que abre o painel logado se põe em dia com os avisos.
     *
     * Aqui, e não no `main.tsx`, porque só depois da guarda desta rota existe
     * sessão — e a inscrição precisa do token para ser gravada no servidor.
     */
    void sincronizarAvisos();
    return () => {
      soltarDespertar();
      soltarReconexao();
    };
  }, [cliente]);

  function abrirArquivo(caminho: string): void {
    ir(abrirAba({ ...estado, lado: 'explorador' }, caminho));
  }

  async function encerrar(): Promise<void> {
    await fetch(PREFIXO_API + ROTAS.auth.logout, { method: 'POST', credentials: 'include' });
    fecharSocketDoPainel();
    sair();
    void navegar({ to: '/entrar' });
  }

  // No celular não há moldura de três colunas. A tela de trabalho tem layout
  // próprio (`NoCelular`, devolvido pela rota `/`); as telas gerais viram um
  // cabeçalho e o conteúdo, porque no telefone elas são consulta, não trabalho.
  if (!telaLarga) {
    if (naTelaDeTrabalho) return <Outlet />;
    return (
      <div className="flex h-dvh flex-col overflow-hidden bg-zinc-950 font-sans text-zinc-100 antialiased">
        <header className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-3 py-2">
          <Link to="/" className="font-semibold">
            Remoto
          </Link>
          <button onClick={() => void encerrar()} className="text-xs text-zinc-500">
            Sair
          </button>
        </header>
        {/* A margem e a rolagem vêm da `Tela` de cada página. */}
        <main className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </main>
      </div>
    );
  }

  const lateral =
    estado.lado === 'explorador' ? (
      <Explorador
        maquinaId={estado.maquina}
        pasta={estado.pasta}
        arquivoAtivo={estado.aba}
        pastaDaSessao={sessaoAtual?.cwd ?? ''}
        tocados={tocados}
        aoTrocarMaquina={(id) => ir({ ...estado, maquina: id, pasta: '', abas: [], aba: '' })}
        aoAbrirPasta={(caminho) => ir({ ...estado, pasta: caminho })}
        aoAbrirArquivo={(caminho) => ir(abrirAba(estado, caminho))}
      />
    ) : (
      <ListaDeSessoes sessaoAtiva={estado.sessao} aoEscolher={trocarDeSessao} />
    );

  return (
    <ProvedorDoWorkspace value={{ estado, ir, abrirArquivo }}>
      <Shell
        estado={estado}
        naTelaDeTrabalho={naTelaDeTrabalho}
        semArquivoAberto={estado.abas.length === 0}
        lateral={lateral}
        centro={<Outlet />}
        direita={
          estado.sessao ? (
            <PainelDaSessao
              id={estado.sessao}
              aoAbrirArquivo={(caminho) => {
                setTocados((atuais) => new Set(atuais).add(caminho));
                abrirArquivo(caminho);
              }}
              aoContinuarNaPasta={(sessao: Sessao) =>
                void navegar({ to: '/sessoes/nova', search: { cwd: sessao.cwd } })
              }
              // A conversa nasceu agora: mesma máquina e mesma pasta da que
              // ficou no editor, então o resto do estado do workspace fica.
              aoIrParaSessao={(sessaoId) => ir({ ...estado, sessao: sessaoId })}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-zinc-600">
              Escolha uma sessão na lista à esquerda.
            </div>
          )
        }
      />

      {/*
        A Prosa entra como SOBREPOSIÇÃO, no molde do `PainelDeAgentes`.

        Nada desmonta: esta moldura monta uma vez (ver o docblock lá em cima), e
        pôr a Prosa por cima em vez de no lugar do Shell é o que faz sair dela
        devolver o workspace exato — mesmas abas, mesmas larguras, mesma rolagem,
        sem recarregar nada. Trocar o Shell pela Prosa seria mais fácil de
        escrever e criaria a segunda interface que este trabalho existe para não
        criar.
      */}
      {estado.prosa && (estado.sessao || mestre) ? (
        <div className="fixed inset-0 z-40">
          <TelaDaProsa
            sessaoId={estado.sessao || (mestre?.id ?? '')}
            aoSair={() => ir({ ...estado, prosa: false })}
            aoIrParaSessao={(sessaoId) => ir({ ...estado, sessao: sessaoId, prosa: false })}
            // Trocar de mestre DENTRO da Prosa: a tela continua aberta, a
            // conversa e que muda.
            aoTrocarDeSessao={(sessaoId) => ir({ ...estado, sessao: sessaoId, prosa: true })}
          />
        </div>
      ) : null}
    </ProvedorDoWorkspace>
  );
}

/**
 * Rota de layout, sem caminho próprio.
 *
 * Todas as telas de dentro do painel penduram aqui, e com elas a guarda de
 * sessão — que antes estava copiada em dez arquivos. `/entrar` continua fora,
 * filha da raiz: é a única tela que existe sem sessão.
 */
export const rotaPainel = createRoute({
  getParentRoute: () => rotaRaiz,
  id: '_painel',
  validateSearch: (busca: Record<string, unknown>) => busca,
  beforeLoad: async () => {
    if (!(await garantirSessao())) throw redirect({ to: '/entrar' });
  },
  component: Painel,
});
